/**
 * Money. Every amount in this system is an integer number of piastres.
 *
 * Why: 0.1 + 0.2 !== 0.3 in binary floating point. A gym that takes 2,500 EGP
 * a hundred times a month will drift, and the drift lands in someone's balance.
 * Integers cannot drift. The ONLY place a decimal exists is at the edges —
 * parsing what staff type, and formatting what they read.
 *
 * 1 EGP = 100 piastres.
 */

export const PIASTRES_PER_EGP = 100;
export const CURRENCY_CODE = 'EGP';

/**
 * Parse what a human typed into piastres.
 *
 * Deliberately strict: returns null rather than guessing. "2,500", "2500.5"
 * and "٢٥٠٠" (Arabic-Indic digits) all parse; "abc", "2.555" and "-5" do not.
 * A null must be surfaced as a validation error, never coerced to 0 — silently
 * turning a typo into a zero payment is how money goes missing.
 */
export function parseEgpToPiastres(input: string): number | null {
  if (typeof input !== 'string') return null;

  // Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹) digits to ASCII, so an
  // Arabic keyboard produces the same number as a Latin one.
  const ascii = input.replace(/[٠-٩۰-۹]/g, (d) =>
    String(
      (d.charCodeAt(0) >= 0x06f0 ? d.charCodeAt(0) - 0x06f0 : d.charCodeAt(0) - 0x0660)
    )
  );

  // Strip thousands separators, spaces (incl. Arabic thousands sep U+066C) and
  // normalise the Arabic decimal separator U+066B to '.'.
  const cleaned = ascii
    .replace(/[\s,٬]/g, '')
    .replace(/٫/g, '.')
    .trim();

  if (cleaned === '') return null;
  // Up to two decimal places. No sign: negative money is not a thing staff type.
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;

  const [whole = '0', frac = ''] = cleaned.split('.');
  const piastres = frac.padEnd(2, '0');

  // Built from digit strings, so no float ever touches the value.
  const result = Number(whole) * PIASTRES_PER_EGP + Number(piastres);
  return Number.isSafeInteger(result) ? result : null;
}

/**
 * Convert whole pounds to piastres. For seed data and prices set in round
 * numbers — use parseEgpToPiastres for anything a human typed.
 */
export function egpToPiastres(egp: number): number {
  return Math.round(egp * PIASTRES_PER_EGP);
}

export function piastresToEgp(piastres: number): number {
  return piastres / PIASTRES_PER_EGP;
}

/**
 * Format for display: "2,500.00 EGP".
 *
 * @param opts.showCurrency  append " EGP" (default true)
 * @param opts.hideZeroFraction  render "2,500 EGP" when the piastres are .00,
 *                               which reads better on dashboard cards
 */
export function formatEgp(
  piastres: number,
  opts: { showCurrency?: boolean; hideZeroFraction?: boolean } = {}
): string {
  const { showCurrency = true, hideZeroFraction = false } = opts;

  const negative = piastres < 0;
  const abs = Math.abs(Math.trunc(piastres));
  const whole = Math.floor(abs / PIASTRES_PER_EGP);
  const frac = abs % PIASTRES_PER_EGP;

  const grouped = whole.toLocaleString('en-US');
  const body =
    hideZeroFraction && frac === 0
      ? grouped
      : `${grouped}.${String(frac).padStart(2, '0')}`;

  return `${negative ? '−' : ''}${body}${showCurrency ? ` ${CURRENCY_CODE}` : ''}`;
}

/** Sum a list of piastre amounts. Integer-safe. */
export function sumPiastres(amounts: readonly number[]): number {
  return amounts.reduce((total, a) => total + a, 0);
}
